const mountedFields = new Map()

const normalizeFieldId = (fieldId) => String(fieldId)

const hasMountedField = (fieldId) => mountedFields.has(normalizeFieldId(fieldId))

const getMountedField = (fieldId) => mountedFields.get(normalizeFieldId(fieldId))

export const isElementVisible = (element) => {
    if (!element || !element.isConnected) {
        return false
    }

    let currentElement = element
    while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE) {
        const style = getComputedStyle(currentElement)
        if (
            style.display === "none" ||
            style.visibility === "hidden" ||
            style.visibility === "collapse"
        ) {
            return false
        }
        currentElement = currentElement.parentElement
    }

    return true
}

const findAmoFieldElement = (fieldId) => {
    const normalizedId = normalizeFieldId(fieldId)
    return document.querySelector(`[data-id="${normalizedId}"]`)
}

export const prepareAmoFieldMount = (fieldId) => {
    const normalizedId = normalizeFieldId(fieldId)
    if (mountedFields.has(normalizedId)) {
        return { fieldId: normalizedId }
    }

    const element = findAmoFieldElement(normalizedId)
    if (!element || !element.parentNode) {
        return null
    }

    const placeholder = document.createComment(`amo-field-${normalizedId}`)
    element.parentNode.insertBefore(placeholder, element)

    mountedFields.set(normalizedId, {
        element,
        placeholder,
        originalParent: placeholder.parentNode,
        originalDisplay: element.style.display,
        mountedContainer: null,
    })

    return { fieldId: normalizedId }
}

export const mountAmoField = (fieldId, container) => {
    const normalizedId = normalizeFieldId(fieldId)
    if (!mountedFields.has(normalizedId)) {
        prepareAmoFieldMount(normalizedId)
    }

    const record = mountedFields.get(normalizedId)
    if (!record || !container) {
        return false
    }

    const displayValue = record.originalDisplay || "flex"
    const isAlreadyInTargetContainer = record.element.parentNode === container

    if (isAlreadyInTargetContainer) {
        record.element.style.display = displayValue
        record.mountedContainer = container
        return true
    }

    const isMountedInAnotherVisibleContainer = (
        record.mountedContainer &&
        record.mountedContainer !== container &&
        record.element.parentNode === record.mountedContainer &&
        isElementVisible(record.mountedContainer)
    )

    if (isMountedInAnotherVisibleContainer) {
        record.element.style.display = displayValue
        return true
    }

    container.replaceChildren(record.element)
    record.element.style.display = displayValue
    record.mountedContainer = container
    return true
}

export const restoreAmoField = (fieldId) => {
    const normalizedId = normalizeFieldId(fieldId)
    const record = mountedFields.get(normalizedId)
    if (!record) {
        return false
    }

    const targetParent = record.placeholder.parentNode || record.originalParent
    if (targetParent) {
        targetParent.insertBefore(record.element, record.placeholder.parentNode ? record.placeholder : null)
    }

    if (record.placeholder.parentNode) {
        record.placeholder.parentNode.removeChild(record.placeholder)
    }

    record.element.style.display = record.originalDisplay
    mountedFields.delete(normalizedId)
    return true
}

export const restoreAmoFields = (fieldIds = []) => {
    fieldIds.forEach((fieldId) => restoreAmoField(fieldId))
}

export const restoreAllAmoFields = () => {
    Array.from(mountedFields.keys()).forEach((fieldId) => restoreAmoField(fieldId))
}

export const syncAmoFieldMount = (fieldId, container) => {
    if (!fieldId || !container) {
        return false
    }

    if (isElementVisible(container)) {
        return mountAmoField(fieldId, container)
    }

    const record = getMountedField(fieldId)
    const isMountedInCurrentContainer = record && (
        record.mountedContainer === container ||
        record.element.parentNode === container
    )

    if (hasMountedField(fieldId) && isMountedInCurrentContainer) {
        restoreAmoField(fieldId)
    }

    return false
}
